import { captureException } from "@sentry/browser";
import type { BuilderDocumentType } from "api-types/modelTypes";
import { capitalize } from "common/utils/capitalize";
import { isSafari } from "common/utils/context";
import { formatDate } from "common/utils/dates";
import downloadFile from "common/utils/downloadFile";
import { toTitleCase } from "common/utils/strings";
import type { AnalyticsEventMetadata } from "lib/analytics";
import type { ExportPlanEvent } from "lib/components/builders/communities/utils/exportPlanTypes";
import ProgressBar from "lib/components/design/ProgressBar";
import type { DownloadInitializer } from "lib/hooks/useDownloadState";
import useDownloadState from "lib/hooks/useDownloadState";
import { termsOfServicePath } from "lib/routeHelpers";
import type { BuildingGenerationFailureDetails } from "lib/workers/buildingGenerationDiagnostic";
import { readFailureDetails, serializeFailureDetails } from "lib/workers/buildingGenerationDiagnostic";
import { BUILDING_GENERATION_USER_MESSAGE, PDFErrorType, type PDFWorkerError } from "lib/workers/pdfWorkerMessages";
import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import Button from "ui/buttons/Button";
import Modal from "ui/components/Modal";
import type { ModalProps } from "ui/components/Modal";
import useAppContext from "ui/hooks/useAppContext";
import IconNotes from "ui/icons/IconNotes";
import { Message } from "ui/text/Message";
import ErrorModal from "./ErrorModal";
import styles from "./Modal.module.scss";

export interface DownloadProgressPlanModalProps<T> extends ModalProps {
  initDownload: DownloadInitializer<T>;
  downloadParams: T;
  documentType: BuilderDocumentType;
  entityLockedAt?: Date | null;
  action?: "download" | "preview";
  children?: ReactNode;
  sendAnalytics?: (eventName: ExportPlanEvent, metadata?: AnalyticsEventMetadata) => void;
  tosVariant?: "body" | "footer";
  modalClassName?: string;
}

/** Builds the JSON artifact a user can send to support after a generation failure. */
export function buildErrorArtifact(details: BuildingGenerationFailureDetails): { filename: string; json: string } {
  const date = new Date().toISOString().slice(0, 10);
  return {
    filename: `plan-set-error-${date}.json`,
    json: JSON.stringify(serializeFailureDetails(details), null, 2),
  };
}

function isBuildingGenerationFailure(error: PDFWorkerError): boolean {
  return Boolean(
    error.details || ("isBuildingGenerationFailure" in error && error.isBuildingGenerationFailure === true),
  );
}

export default function DownloadProgressPlanModal<T>({
  open,
  onClose,
  initDownload,
  downloadParams,
  documentType,
  entityLockedAt,
  sendAnalytics,
  action = "download",
  children = null,
  size = "small",
  tosVariant = "body",
  modalClassName,
}: DownloadProgressPlanModalProps<T>) {
  const worker = useRef<Worker>(null);
  const { setUserNotification } = useAppContext();
  const [{ error, progress, downloading, safari }, dispatch] = useDownloadState();
  const actionGerund = `${toTitleCase(action)}ing`;

  /**
   * Check for Safari after component mounts. Necessary due to
   * inability for Safari to spawn workers from within another worker.
   * See: https://www.chromestatus.com/feature/6080438103703552
   */
  useEffect(() => {
    dispatch({ type: "SET_SAFARI", payload: isSafari() });
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
  }, [open]);

  const closeModal = () => {
    onClose?.();
    dispatch({ type: "RESET" });
  };

  const handleDownload = async () => {
    try {
      dispatch({ type: "SET_DOWNLOADING", payload: true });
      await initDownload({
        worker,
        setUserNotification,
        downloadParams,
        onProgress: (progress) => dispatch({ type: "SET_PROGRESS", payload: progress }),
        onClose: closeModal,
        safari,
        onError: (error) => {
          if (error.errorType === PDFErrorType.INTERNAL) {
            captureException(error, {
              extra: {
                documentType,
                action,
                safari,
              },
            });
          }
          dispatch({ type: "SET_ERROR", payload: error });
        },
        sendAnalytics,
      });
    } catch (error) {
      console.error(error);
      dispatch({
        type: "SET_ERROR",
        payload: {
          errorType: PDFErrorType.INTERNAL,
          message: error instanceof Error ? error.message : String(error),
          details: readFailureDetails(error),
        },
      });
    }
  };

  if (error) {
    console.error(error);
    const isGenerationFailure = isBuildingGenerationFailure(error);
    let errorMessage: string;
    if (isGenerationFailure) {
      errorMessage = BUILDING_GENERATION_USER_MESSAGE;
    } else {
      errorMessage =
        error.errorType === PDFErrorType.USER && error.message
          ? error.message
          : `An error occurred and your ${documentType} could not be generated. Please try again and contact us if the issue remains unresolved.`;
    }

    const failureDetails = error.details;
    const downloadErrorAction = failureDetails ? (
      <Button
        btnStyle="primary"
        btnSize="large"
        onClick={() => {
          const artifact = buildErrorArtifact(failureDetails);
          downloadFile(artifact.filename, artifact.json, "application/json");
        }}
      >
        Download error info file
      </Button>
    ) : undefined;

    return (
      <ErrorModal
        errorTitle={
          isGenerationFailure || error.errorType === PDFErrorType.INTERNAL ? "Unexpected Internal Error" : "Error"
        }
        errorMessage={errorMessage}
        onClose={closeModal}
        action={downloadErrorAction}
      />
    );
  }

  const submitDisabled = downloading;

  const termsOfService = (
    <span>
      {`Clicking "${capitalize(action)}"`} indicates that you have read and agree to our{" "}
      <a href={termsOfServicePath} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
        Terms of Service
      </a>
      .
    </span>
  );

  return (
    <Modal
      title={`Download ${documentType}`}
      onClose={onClose}
      open={!!open}
      size={size}
      className={modalClassName}
      noCloseOnOutsideClick={safari}
      variants={["footerButtons"]}
      footer={
        <>
          {tosVariant === "footer" && <span className={styles.footerTerms}>{termsOfService}</span>}
          <Button btnStyle="tertiary" btnSize="large" onClick={onClose}>
            Cancel
          </Button>
          <Button
            btnStyle="primary"
            btnSize="large"
            disabled={submitDisabled}
            onClick={handleDownload}
            title={downloading ? `${actionGerund}...` : undefined}
          >
            {downloading ? `${actionGerund}...` : toTitleCase(action)}
          </Button>
        </>
      }
    >
      <div className={styles.modalBodyContainer}>
        <div className="modal-body">
          <div className={styles.wrapper}>
            {downloading && (
              <div className="progress-wrapper">
                <div className="progress-wrapper-text">
                  {actionGerund} {documentType}...
                </div>
                <ProgressBar progress={progress} />
              </div>
            )}
            {entityLockedAt && (
              <Message messageType="warning">
                <span>
                  Home Plan locked — Plan Set PDF will reflect the state of the plan as of{" "}
                  {formatDate(entityLockedAt, "WITH_TIME")}.
                </span>
              </Message>
            )}
            {tosVariant === "body" && (
              <>
                <div className="icon-wrapper">
                  <div className="icon-background-circle">
                    <IconNotes size={26} />
                  </div>
                </div>
                <div className="message-row">
                  <div className="message-text">{termsOfService}</div>
                </div>
              </>
            )}
          </div>
          {children}
        </div>
      </div>
    </Modal>
  );
}
